import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as amplitude from '@amplitude/analytics-browser';
import { v4 } from 'uuid';
import { initRefresh, type AuthInfo } from '@fhl-world/auth-core/webpack-esm';

import RoutePage from './pages/RoutePage/RoutePage';
import LoadingScreen from './components/LoadingScreen/LoadingScreen';
import InactivityModal from './components/InactivityModal';
import { REACT_APP_AMPLITUDE_API_KEY } from '@utils/common';
import { extractRedirectPath } from '@config/routes';
import { AUTH_CONFIG } from '@config/authConfig';

function App() {
	const navigate = useNavigate();

	const [authInfo, setAuthInfo] = useState<AuthInfo | null>(null);

	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const handleShowModal = useCallback(() => setIsModalOpen(true), []);
	const handleCloseModal = useCallback(() => setIsModalOpen(false), []);

	useEffect(() => {
		initRefresh({
			...AUTH_CONFIG,
			onUserActive: handleCloseModal,
			onUserInactive: handleShowModal,
		}).then(setAuthInfo);
	}, []);

	useEffect(() => {
		if (!authInfo) {
			return;
		}
		try {
			amplitude.init(
				REACT_APP_AMPLITUDE_API_KEY,
				authInfo.tokenPayload.claims.iid,
				{
					minIdLength: 1,
					defaultTracking: { sessions: true },
				}
			);

			const uuid = v4();
			const identifyEvent = new amplitude.Identify();
			identifyEvent.set('salmonsession_id', uuid);
			amplitude.identify(identifyEvent);

			window.salmonsession_id = uuid;
		} catch (error) {
			console.error('Failed to send amplitude init event', error);
		}
	}, [authInfo]);

	useEffect(() => {
		if (!authInfo) {
			return;
		}
		const redirectPath = extractRedirectPath();
		if (redirectPath) {
			navigate(redirectPath);
		}
	}, [authInfo]);

	if (authInfo) {
		return (
			<>
				<RoutePage user={authInfo.user} />
				<InactivityModal open={isModalOpen} />
			</>
		);
	} else {
		return <LoadingScreen />;
	}
}

export default App;
